import { useEffect, useState } from "react";

import { css, keyframes } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src";
import { Video } from "@/components/wpl/video/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";
import { LogoV3 } from "@/libraries/wpl/foundations/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
/* arrows */

type ArrowProps = {
    color?: string;
    rotate?: number | string;
};

const ArrowPencilPointRight = (props: ArrowProps) => {
    return (
        <svg
            style={{
                color: props.color ? props.color : "currentColor",
                transform: `rotate(${props.rotate ? props.rotate : "0"}deg)`
            }}
            css={svgStyles}
            width="50"
            height="17"
            viewBox="0 0 72 25"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill="currentColor"
                d="M59.938 15.4c-.92-.48-1.816-.597-2.7-.75a7.975 7.975 0 0 0-2.872.044c-.848.163-1.709.062-2.511-.19-1.5-.473-3.039-.55-4.597-.555-.973-.005-1.959-.054-2.916-.202-.663-.104-1.292-.416-1.965-.642-.392.625-.992.599-1.612.367-1.278-.481-2.594-.57-3.947-.543-.736.014-1.463-.158-2.068-.635-.077-.056-.175-.084-.267-.117-.61.602-1.323.564-2.063.397-1.19-.262-2.387-.5-3.577-.762-.804-.18-1.606-.377-2.409-.569-.797-.196-1.56-.48-2.43-.276-1.08.253-2.16-.009-3.235-.18-1.226-.2-2.439-.518-3.666-.604-2.097-.152-4.158-.522-6.235-.81-.993-.14-1.991-.243-2.975-.423a42.808 42.808 0 0 1-2.978-.664c-1.046-.264-2.085-.582-3.121-.882C1.122 7.214.74 6.67.444 6.112.36 5.953.59 5.534.78 5.337c.594-.621 1.35-.92 2.211-.987 1.934-.153 3.86-.06 5.774.12 1.72.162 3.425.459 5.124.761 1.963.342 3.938.566 5.928.725 1.551.12 3.096.346 4.645.495 1.447.144 2.9.241 4.353.38 1.607.156 3.215.305 4.824.502 2.28.273 4.55.593 6.831.872 1.62.199 3.241.391 4.895.293.938-.058 1.897.126 2.828.295 1.34.24 2.692.28 4.046.348 1.651.082 3.303.158 4.96.246.474.026.944.098 1.42.124.13.013.267-.05.401-.072-.052-.126-.069-.295-.162-.37-.553-.454-1.127-.874-1.68-1.327-1.467-1.208-2.934-2.416-4.394-3.635-.563-.473-.885-1.124-1.227-1.76-.208-.387-.023-.71.165-1.002.18-.28.301-.676.723-.71.29-.024.579-.048.87-.041 1.45.024 2.74.566 4.009 1.184.906.442 1.796.925 2.718 1.344.386.178.62.43.782.807.114.264.204.591.615.274.182-.137.404-.09.571.125.246.314.537.602.782.921.167.222.358.277.63.3 1.026.064 1.83.574 2.406 1.407.371.537.868.9 1.432 1.15.655.29 1.24.663 1.702 1.185.85.958 1.653 1.953 2.45 2.954.341.43.245 1.248-.147 1.657-.703.737-1.431 1.44-2.117 2.191-.368.406-.66.885-1 1.323-.14.178-.285.415-.478.478-.75.258-1.239.837-1.802 1.342-.233.21-.48.442-.76.557-.915.379-1.838.732-2.774 1.043-.273.091-.606-.027-.91-.023-.139.004-.345.03-.402.12-.507.805-1.262 1.2-2.163 1.25-1.377.08-2.673.52-4.003.82-1.53.344-3.061.683-4.589 1.052a13.66 13.66 0 0 1-3.57.36c-.512-.011-.884-.266-1.065-.674-.39-.899-.488-.879.139-1.69.118-.155.253-.297.35-.466.372-.67 1.031-.793 1.682-1.007 1.115-.364 2.208-.796 3.296-1.234 1.425-.578 2.831-1.193 4.246-1.784.764-.322 1.542-.601 2.29-.949.75-.348 1.47-.753 2.287-1.178l.016-.034z"
            />
        </svg>
    );
};

const ArrowPencilPointLeft = (props: ArrowProps) => {
    return (
        <svg
            style={{
                color: props.color ? props.color : "currentColor",
                transform: `rotate(${props.rotate ? props.rotate : "0"}deg)`
            }}
            css={svgStyles}
            width="50"
            height="17"
            viewBox="0 0 72 24"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill="currentColor"
                d="M11.923 9.891c.933.429 1.83.508 2.72.622a8.268 8.268 0 0 0 2.87-.149c.843-.189 1.706-.123 2.515.09 1.513.401 3.053.417 4.61.365.973-.031 1.96-.021 2.921.086.666.076 1.303.353 1.983.546.374-.617.975-.614 1.6-.413 1.292.416 2.61.453 3.962.376.735-.04 1.467.098 2.084.536.08.051.178.075.271.104.594-.604 1.307-.594 2.051-.46 1.197.208 2.4.393 3.597.601.809.144 1.616.304 2.424.46.802.16 1.572.404 2.437.176 1.072-.285 2.16-.072 3.24.053 1.23.147 2.452.409 3.68.446 2.102.069 4.172.35 6.256.55.997.097 1.998.16 2.986.297 1.006.139 2.007.323 2.995.53 1.053.216 2.101.484 3.145.735.677.158 1.073.668 1.385 1.196.088.15-.132.564-.316.76-.577.622-1.325.94-2.184 1.035-1.93.22-3.856.202-5.776.099-1.724-.091-3.436-.315-5.143-.543a66.397 66.397 0 0 0-5.946-.479c-1.554-.057-3.105-.218-4.657-.304-1.451-.086-2.906-.126-4.363-.206-1.611-.09-3.223-.174-4.836-.304-2.288-.179-4.566-.403-6.853-.588-1.626-.13-3.252-.256-4.902-.1-.936.091-1.9-.05-2.835-.179-1.348-.183-2.7-.17-4.055-.186-1.653-.017-3.307-.029-4.965-.052-.476-.007-.947-.06-1.423-.067-.13-.008-.267.059-.4.085.055.119.077.282.172.351.565.417 1.151.8 1.716 1.218 1.5 1.11 2.999 2.222 4.491 3.344.576.435.915 1.051 1.274 1.651.218.367.042.685-.137.974-.173.278-.284.664-.705.712-.288.034-.577.068-.869.072-1.449.03-2.753-.444-4.039-.993-.918-.393-1.821-.825-2.753-1.196a1.417 1.417 0 0 1-.805-.75c-.12-.25-.219-.562-.621-.24-.18.138-.402.1-.575-.1-.255-.294-.553-.561-.807-.86-.172-.207-.365-.254-.638-.266-1.027-.024-1.844-.485-2.444-1.268-.385-.504-.892-.836-1.462-1.057-.662-.256-1.258-.593-1.734-1.08a69.905 69.905 0 0 1-2.53-2.758c-.352-.404-.277-1.196.103-1.606.683-.737 1.392-1.443 2.058-2.192.356-.405.635-.879.964-1.314.135-.177.274-.41.464-.48.744-.276 1.217-.853 1.766-1.361.227-.211.467-.445.745-.566.904-.4 1.817-.775 2.745-1.11.27-.098.606.004.911-.011.138-.01.343-.042.398-.131.486-.796 1.23-1.205 2.129-1.287 1.374-.128 2.659-.601 3.98-.94 1.52-.39 3.042-.773 4.56-1.186C22.527.86 23.711.715 24.915.7c.513-.009.892.223 1.084.61.415.853.512.83-.093 1.635-.115.155-.246.297-.338.464-.354.66-1.01.803-1.654 1.034-1.105.393-2.186.85-3.262 1.314-1.41.61-2.798 1.257-4.197 1.879-.755.34-1.525.638-2.265 1.001-.74.364-1.448.781-2.254 1.222l-.014.033z"
            />
        </svg>
    );
};

export const AtlassianIntelligence = () => {
    const intl = useIntl();
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    // edit these constant values to move the group of arrows all together
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

    useEffect(() => {
        if (sectionIsVisible) {
            setTimeout(() => {
                // timeout to wait to play video until animation has finished before playing video.
                setShouldLoadVideo(true);
            }, 1000);
        } else {
            setShouldLoadVideo(false);
        }
    }, [sectionIsVisible, shouldLoadVideo]);

    const useNewScreenshots = checkFeatureFlagGate(
        featureFlags.gwp9214HomepageScreenshots
    );

    const ctaButtonLabel = intl.formatMessage({
        id: "HomePageTemplateV2.AtlassianIntelligence.CtaButtonAriaLabel",
        defaultMessage: "Explore Rovo"
    });

    const videoURL = useNewScreenshots
        ? "https://dam-cdn.atl.orangelogic.com/AssetLink/8j6w234e674531msl34e178mgn42mne7.mp4"
        : "https://dam-cdn.atl.orangelogic.com/AssetLink/438r2lhmec7014pn861g30w8kx28ty08.mp4";
    const videoLink = shouldLoadVideo ? videoURL : "";

    const videoPreviewImage = useNewScreenshots
        ? "https://dam-cdn.atl.orangelogic.com/AssetLink/kb8y65md2n1k708ab2k8i1jcvm2qgjov.webp"
        : "https://dam-cdn.atl.orangelogic.com/AssetLink/mo4k25h6c2lnm475w305ja5q6ctq885v.webp";
    return (
        <UFOSegment name="homepage-v2-atlassianIntelligence">
            <section
                id="atlassian-intelligence"
                css={sectionStyles}
                ref={scrollRef}
                className={sectionIsVisible ? "is-visible" : ""}
            >
                <Row layout="12">
                    <div css={css({ position: "relative" })}>
                        <div className="background" css={backgroundStyles} />
                        <div className="content-wrapper" css={wrapperStyles}>
                            <div css={containerStyles}>
                                <div css={textWrapperStyles}>
                                    <div
                                        className="branding"
                                        css={brandingStyles}
                                    >
                                        <LogoV3
                                            brand="rovo"
                                            type="logo"
                                            appearance="brand"
                                            width={126}
                                            height={40}
                                            lightMode={true}
                                        />
                                    </div>
                                    <h2
                                        className="h2 heading"
                                        css={headerStyles}
                                    >
                                        {intl.formatMessage({
                                            id: "HomePageTemplateV2.AtlassianIntelligence.Header",
                                            defaultMessage:
                                                "Transform teamwork with human-AI collaboration"
                                        })}
                                    </h2>

                                    <Link
                                        aria-label={ctaButtonLabel}
                                        text={ctaButtonLabel}
                                        bgColor="bgBlackBlue"
                                        href={useLocalizedHref(
                                            "/software/rovo"
                                        )}
                                        dataEventProps={{
                                            "data-event": "clicked",
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container":
                                                "ctaSection",
                                            "data-product-key": "home",
                                            "data-label-english":
                                                "Explore Atlassian Intelligence"
                                        }}
                                    />
                                </div>
                                <div css={videoWrapperStyles}>
                                    <LeftArrows
                                        sectionIsVisible={sectionIsVisible}
                                    />
                                    <RightArrows
                                        sectionIsVisible={sectionIsVisible}
                                    />
                                    <div css={videoInnerWrapperStyles}>
                                        <Video
                                            videoLink={videoLink}
                                            videoType="webm"
                                            videoPreviewImage={
                                                videoPreviewImage
                                            }
                                            loop={false}
                                        >
                                            <Picture
                                                className="fallback-img--desktop"
                                                image={{
                                                    src: videoPreviewImage,
                                                    alt: "",
                                                    width: 1920,
                                                    height: 1080
                                                }}
                                            />
                                        </Video>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Row>
            </section>
        </UFOSegment>
    );
};

type ArrowsProps = {
    sectionIsVisible: boolean;
};

const LeftArrows = ({ sectionIsVisible }: ArrowsProps) => {
    return (
        <div css={[leftAndRightArrowsStyles, leftArrowsStyles]}>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowLeft1Animation
                ]}
            >
                <ArrowPencilPointLeft
                    color={wacToken("color.primary.blue")}
                    rotate={200}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowLeft2Animation
                ]}
            >
                <ArrowPencilPointLeft
                    color={wacToken("color.primary.purple")}
                    rotate={222}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowLeft3Animation
                ]}
            >
                <ArrowPencilPointLeft
                    color={wacToken("color.neutral.light0")}
                    rotate={219}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowLeft4Animation
                ]}
            >
                <ArrowPencilPointLeft
                    color={wacToken("color.primary.black")}
                    rotate={196}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowLeft5Animation
                ]}
            >
                <ArrowPencilPointLeft
                    color={wacToken("color.extended.lime300")}
                    rotate={199}
                />
            </div>
        </div>
    );
};

const RightArrows = ({ sectionIsVisible }: ArrowsProps) => {
    return (
        <div css={[leftAndRightArrowsStyles, rightArrowsStyles]}>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowRight1Animation
                ]}
            >
                <ArrowPencilPointRight
                    color={wacToken("color.primary.blue")}
                    rotate={139}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowRight2Animation
                ]}
            >
                <ArrowPencilPointRight
                    color={wacToken("color.neutral.light0")}
                    rotate={127}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowRight3Animation
                ]}
            >
                <ArrowPencilPointRight
                    color={wacToken("color.primary.lime")}
                    rotate={114}
                />
            </div>
            <div
                css={[
                    arrowStyles,
                    fadeInAnimation,
                    sectionIsVisible && arrowRight4Animation
                ]}
            >
                <ArrowPencilPointRight
                    color={wacToken("color.primary.black")}
                    rotate={138}
                />
            </div>
        </div>
    );
};
const sectionStyles = css({
    paddingBlockEnd: "60px",
    overflow: "hidden"
});

const arrowStyles = css({
    position: "absolute",
    animationDuration: "1.5s, .3s",
    animationIterationCount: "1",
    animationFillMode: "forwards"
});

const wrapperStyles = css({
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    position: "relative"
});

const leftAndRightArrowsStyles = css({
    position: "absolute",
    height: "100%",
    width: "100%",
    insetBlockStart: 0,
    pointerEvents: "none",
    zIndex: 2
});

const svgStyles = css({
    [media.below.sm]: {
        height: "9px",
        width: "24px"
    },
    [media.only.sm]: {
        height: "17px",
        width: "50px"
    }
});

const leftArrowsStyles = css({
    insetBlockStart: "-0.4%",
    insetInlineStart: "-1.3%",
    [media.only.sm]: {
        insetBlockStart: "-54%",
        insetInlineStart: "1.6%"
    },
    [media.only.xs]: {
        insetBlockStart: "-60%",
        insetInlineStart: "1.6%"
    },
    [media.only.xxs]: {
        insetBlockStart: "-60%",
        insetInlineStart: "1.6%"
    }
});

const rightArrowsStyles = css({
    insetBlockStart: "-1.2%",
    insetInlineStart: "1.5%",
    [media.only.sm]: {
        insetBlockStart: "-14%",
        insetInlineStart: "-4.7%"
    },
    [media.only.xs]: {
        insetBlockStart: "-20%",
        insetInlineStart: "-4.7%"
    },
    [media.only.xxs]: {
        insetBlockStart: "-20%",
        insetInlineStart: "-4.7%"
    }
});

const headerStyles = css({
    zIndex: 3,
    margin: `0 0 ${token("space.300")}`,
    [media.below.sm]: {
        maxWidth: "80%"
    },
    [`&.heading`]: {
        color: wacToken("color.neutral.light0")
    }
});

const containerStyles = css({
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: "55px",
    maxWidth: "952px",
    paddingBlockStart: "66px",
    position: "relative",
    textAlign: "center",

    [media.below.sm]: {
        gap: "24px"
    }
});

const backgroundStyles = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: "50%",
    transform: "translateX(-50%)",
    width: "1152px",
    maxWidth: "100vw",
    height: "100%",
    backgroundColor: wacToken("color.primary.black"),
    zIndex: "-1",
    clipPath: "polygon(330% 0,0 0,0 100%,0 95%)"
});

const textWrapperStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    [media.below.sm]: {
        marginBlockEnd: "6px"
    }
});

const brandingStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBlockEnd: "32px"
});

const videoWrapperStyles = css({
    position: "relative",
    aspectRatio: "16 / 9",
    width: "100%"
});

const videoInnerWrapperStyles = css({
    overflow: "hidden",
    outlineStyle: "solid",
    outlineColor: wacToken("color.neutral.light100"),
    outlineWidth: "3px",
    borderRadius: "10px",
    lineHeight: 0
});

const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const fadeInAnimation = css({
    animationName: fadeIn
});

const arrowLeft1 = keyframes({
    from: {
        insetInlineStart: "-100%",
        insetBlockStart: "-15%"
    },
    to: {
        insetInlineStart: "-24.5%",
        insetBlockStart: "25%"
    }
});

const arrowLeft1Animation = css({
    animationName: arrowLeft1
});

const arrowLeft2 = keyframes({
    from: {
        insetInlineStart: "-100%",
        insetBlockStart: "-60%"
    },
    to: {
        insetInlineStart: "-5%",
        insetBlockStart: "45%"
    }
});

const arrowLeft2Animation = css({
    animationName: arrowLeft2
});

const arrowLeft3 = keyframes({
    from: {
        insetInlineStart: "-100%",
        insetBlockStart: "-25%"
    },
    to: {
        insetInlineStart: "-12%",
        insetBlockStart: "32%"
    }
});

const arrowLeft3Animation = css({
    animationName: arrowLeft3
});

const arrowLeft4 = keyframes({
    from: {
        insetInlineStart: "-100%",
        insetBlockStart: "-40%"
    },
    to: {
        insetInlineStart: "-21%",
        insetBlockStart: "41%"
    }
});
const arrowLeft4Animation = css({
    animationName: arrowLeft4
});

const arrowLeft5 = keyframes({
    from: {
        insetInlineStart: "-100%",
        insetBlockStart: "-15%"
    },
    to: {
        insetInlineStart: "-10.5%",
        insetBlockStart: "54%"
    }
});

const arrowLeft5Animation = css({
    animationName: arrowLeft5
});

const arrowRight1 = keyframes({
    from: {
        insetInlineEnd: "-100%",
        insetBlockStart: "-40%"
    },
    to: {
        insetInlineEnd: "-13%",
        insetBlockStart: "5%"
    }
});

const arrowRight1Animation = css({
    animationName: arrowRight1
});

const arrowRight2 = keyframes({
    from: {
        insetInlineEnd: "-100%",
        insetBlockStart: "-30%"
    },
    to: {
        insetInlineEnd: "-11%",
        insetBlockStart: "-9%"
    }
});

const arrowRight2Animation = css({
    animationName: arrowRight2
});

const arrowRight3 = keyframes({
    from: {
        insetInlineEnd: "-100%",
        insetBlockStart: "-50%"
    },
    to: {
        insetInlineEnd: "-17%",
        insetBlockStart: "-25%"
    }
});

const arrowRight3Animation = css({
    animationName: arrowRight3
});

const arrowRight4 = keyframes({
    from: {
        insetInlineEnd: "-100%",
        insetBlockStart: "-20%"
    },
    to: {
        insetInlineEnd: "-25%",
        insetBlockStart: "-17%"
    }
});

const arrowRight4Animation = css({
    animationName: arrowRight4
});

AtlassianIntelligence.displayName = "AtlassianIntelligence";
